/**
 * Sidebar/index.tsx
 * desc: navigation v2 — a full-height sidebar with a brand row, a scrollable menu (daily-work items, pinned pages,
 *       Setup), and a bottom menu
 * note: rendered by Layout behind the "web.global.navigationV2.enabled" flag
 */

import { useState, type ComponentType } from 'react';
import { Link } from 'react-router';
import { YmIcon as YmIconUntyped } from '../Brand';
import { SidebarIcon } from '../Icon';
import appUrls from '../../settings/app-urls';
import { trackEvent } from '../../utilities/analytics';
import { translate } from '../../utilities/localization';
import WikiPagesDialog from '../../modules/WikiPages';
import { ANALYTICS_DASHBOARD, BOTTOM_ITEMS, DAILY_ITEMS, SETTINGS_ITEM, WIKI_PAGES } from './config';
import MenuButton from './MenuButton';
import MenuItem from './MenuItem';
import MenuLink from './MenuLink';
import PinnedMenu from './PinnedMenu';
import SetupMenu from './SetupMenu';
import useNavAccess from './hooks/useNavAccess';
import useSidebarCollapsed from './hooks/useSidebarCollapsed';
import './styles.css';

// unconverted JS components infer every destructured prop as required
const YmIcon = YmIconUntyped as unknown as ComponentType<{ className?: string }>;

const Sidebar = () => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    // State
    const [ isWikiPagesOpen, setIsWikiPagesOpen ] = useState(false);
    // Hooks
    const { isCollapsed, toggle } = useSidebarCollapsed();
    const { canAccess, canAccessAction } = useNavAccess();
    // Variables
    const hasWikiPages = canAccessAction(WIKI_PAGES);


    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handleLogoClick = () => trackEvent('Global: Sidebar - YM Logo (Click)');

    const handleToggleClick = () => {
        trackEvent(`Global: Sidebar - ${isCollapsed ? 'Expand' : 'Collapse'} Toggle (Click)`);
        toggle();
    };

    const handleWikiPagesClick = () => setIsWikiPagesOpen(true);


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <nav id="sidebar" className={ isCollapsed ? 'collapsed' : undefined } aria-label={ translate('nav.aria.primary') }>
            <div className="brand">
                { isCollapsed ?
                    // note: static brand mark — it gives way to the toggle on hover (styles.css), so it must not be focusable
                    <YmIcon/>
                    :
                    <>
                        <Link to={ appUrls.HOME } aria-label={ translate('nav.link.home') } onClick={ handleLogoClick }><YmIcon/></Link>
                        <hgroup>
                            <h1>Y Meadows</h1>
                            <h2>{ translate('common.application.name') }</h2>
                        </hgroup>
                    </>
                }
                <SidebarIcon
                    className="toggle"
                    aria-expanded={ !isCollapsed }
                    onClick={ handleToggleClick }
                    tooltip={ translate(isCollapsed ? 'nav.toggle.expand' : 'nav.toggle.collapse') }
                />
            </div>
            <div className="scroll">
                <menu className="daily">
                    { DAILY_ITEMS.filter((item) => canAccess(item)).map((item) => <li key={ item.id }><MenuItem item={ item } isCollapsed={ isCollapsed }/></li>) }
                    { canAccessAction(ANALYTICS_DASHBOARD) ? <li><MenuLink link={ ANALYTICS_DASHBOARD } isCollapsed={ isCollapsed }/></li> : null }
                </menu>

                <PinnedMenu isCollapsed={ isCollapsed }/>
                <SetupMenu isCollapsed={ isCollapsed } canAccess={ canAccess }/>
            </div>
            <menu className="bottom">
                { BOTTOM_ITEMS.filter((item) => canAccess(item)).map((item) => <li key={ item.id }><MenuItem item={ item } isCollapsed={ isCollapsed }/></li>) }
                { hasWikiPages ? <li><MenuButton action={ WIKI_PAGES } isCollapsed={ isCollapsed } onClick={ handleWikiPagesClick }/></li> : null }
                { canAccess(SETTINGS_ITEM) ? <li><MenuItem item={ SETTINGS_ITEM } isCollapsed={ isCollapsed }/></li> : null }
            </menu>

            { hasWikiPages ? <WikiPagesDialog open={ isWikiPagesOpen } onOpenChange={ setIsWikiPagesOpen }/> : null }
        </nav>
    );
};

export default Sidebar;
